import React, {ComponentType, FC} from 'react';

import {useInitialEffect} from '../../libs/shared/core/src/helpers/react/hooks/useEffectHelpers';
import {NewPanelId} from '../../libs/shared/core/src/store/panels/panelStateBase';
import {useAppDispatch, useAppSelector} from '../helpers/appReduxHelpers';
import {AppState} from '../store/appState';
import {ThunkAction} from '../store/appThunk';
import {removePanelAction} from '../store/panels/panelsActions';

/*
 * Props.
 */

interface LegacyTransientPanelProps<TId extends NewPanelId> {
  panelId: TId;
}

/*
 * HOC.
 */

/**
 * Creates/destroys a panel based on the mounting/unmounting of the component.
 *
 * @deprecated To keep a unidirectional data flow, manage panel state in Redux and then render from that state.
 */
export function inLegacyTransientPanel<TId extends NewPanelId, TProps extends LegacyTransientPanelProps<TId>>(
  WrappedComponent: ComponentType<TProps>,
  navigatePanelAction: (props: TProps) => ThunkAction,
): ComponentType<TProps> {
  const Component: FC<TProps> = (props) => {
    const {panelId} = props;
    const panel = useAppSelector((state: AppState) => state.panels[panelId]);

    const dispatch = useAppDispatch();
    useInitialEffect(() => {
      // Create the panel on mount.
      if (navigatePanelAction) {
        dispatch(navigatePanelAction(props));
      }

      // Destroy it on unmount.
      return () => {
        dispatch(removePanelAction(panelId));
      };
    });

    // Until the panel is created, we have nothing to render.
    if (!panel) {
      return null;
    }

    return React.createElement(WrappedComponent, props);
  };

  const displayName = WrappedComponent.displayName || WrappedComponent.name || 'Component';
  Component.displayName = `inTransientPanel(${displayName})`;

  return Component;
}
